import React, { Suspense, lazy, memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { LoadingIndicator } from 'atoms/LoadingIndicator';

import type {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
  LibraryItems,
} from '@excalidraw/excalidraw/types/types';
import type { ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';

import { FullscreenButton } from './FullscreenButton';
import { useFullscreen } from './useFullscreen';

// MUST run at module scope, BEFORE the lazy chunk below is evaluated: the
// library resolves `${EXCALIDRAW_ASSET_PATH}excalidraw-assets/...` (fonts,
// locales, vendor chunk) at module-eval time. Assets are self-hosted from
// public/excalidraw-assets{,-dev}/ — zero requests to unpkg/CDN with a board
// open (privacy requirement, spec 0010).
(window as unknown as { EXCALIDRAW_ASSET_PATH: string }).EXCALIDRAW_ASSET_PATH = `${
  process.env.PUBLIC_URL || ''
}/`;

// ~1MB CJS bundle — loaded on demand so the lesson page itself stays light.
const LazyExcalidraw = lazy(() =>
  import('@excalidraw/excalidraw').then((module) => ({ default: module.Excalidraw })),
);

export interface IExcalidrawCanvas {
  initialData?: ExcalidrawInitialDataState | null;
  viewModeEnabled?: boolean;
  onChange?: (
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => void;
  /** excalidraw 0.17 hands its imperative API through a callback prop. */
  excalidrawApiRef?: (api: ExcalidrawImperativeAPI) => void;
  /**
   * Fires on library add/remove AND on programmatic updateLibrary (echo!) —
   * the editor's sync hook handles suppression. Undefined for viewers/ERP.
   */
  onLibraryChange?: (items: LibraryItems) => void;
  langCode?: string;
  className?: string;
}

export const ExcalidrawCanvas = memo(
  ({
    initialData,
    viewModeEnabled = false,
    onChange,
    excalidrawApiRef,
    onLibraryChange,
    langCode,
    className,
  }: IExcalidrawCanvas) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const { isFullscreen, toggle, isSupported } = useFullscreen(containerRef);

    // Excalidraw renders this slot in view AND edit mode and lays it out
    // alongside its own top-right controls — no overlap, no z-index war.
    const renderTopRightUI = useCallback(
      () =>
        isSupported ? (
          <FullscreenButton
            isFullscreen={isFullscreen}
            onToggle={toggle}
            label={
              isFullscreen ? t('whiteboard.fullscreen.exit') : t('whiteboard.fullscreen.enter')
            }
          />
        ) : null,
      [isFullscreen, isSupported, t, toggle],
    );

    return (
      <CanvasWrapper ref={containerRef} className={className}>
        <Suspense fallback={<LoadingIndicator />}>
          <LazyExcalidraw
            initialData={initialData}
            viewModeEnabled={viewModeEnabled}
            onChange={onChange}
            excalidrawAPI={excalidrawApiRef}
            onLibraryChange={onLibraryChange}
            langCode={langCode}
            renderTopRightUI={renderTopRightUI}
          />
        </Suspense>
      </CanvasWrapper>
    );
  },
);

const CanvasWrapper = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 320px);
  min-height: 500px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 16px;
  overflow: hidden;

  /* spec 0010 rev.2: the school library is server-hosted; the "Browse
     libraries" link is baked into excalidraw 0.17.6 and points at
     libraries.excalidraw.com. Class verified in the dist
     (LibraryMenuBrowseButton renders <a class="library-menu-browse-button">).
     Re-verify the selector on any excalidraw upgrade. */
  .library-menu-browse-button {
    display: none;
  }

  /* In fullscreen the wrapper IS the viewport — drop the framing so the
     canvas actually fills the screen (the height calc above would otherwise
     leave a 320px gap). */
  &:fullscreen,
  &:-webkit-full-screen {
    width: 100vw;
    height: 100vh;
    min-height: 100vh;
    border: none;
    border-radius: 0;
    background: ${Colors.white};
  }
`;
